package com.havn.app.features.openhouses.data

import com.havn.app.core.logging.HavnLogger
import com.havn.app.core.media.havnDisplayPhotoSource
import com.havn.app.features.clients.domain.HavnClientQrPayload
import com.havn.app.features.openhouses.domain.LeadTemperature
import com.havn.app.features.openhouses.domain.OpenHouse
import com.havn.app.features.openhouses.domain.OpenHouseDraft
import com.havn.app.features.openhouses.domain.OpenHousePropertyOption
import com.havn.app.features.openhouses.domain.OpenHouseRepository
import com.havn.app.features.openhouses.domain.OpenHouseStats
import com.havn.app.features.openhouses.domain.OpenHouseStatus
import com.havn.app.features.openhouses.domain.OpenHouseVisitor
import com.havn.app.features.openhouses.domain.VisitorInterest
import com.havn.app.features.openhouses.domain.VisitorSignInDraft
import io.github.jan.supabase.SupabaseClient
import io.github.jan.supabase.postgrest.from
import io.github.jan.supabase.postgrest.postgrest
import io.github.jan.supabase.postgrest.rpc
import io.github.jan.supabase.postgrest.query.Columns
import io.github.jan.supabase.postgrest.query.Order
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlin.time.Clock

class SupabaseOpenHouseRepository(
    private val client: SupabaseClient
) : OpenHouseRepository {
    private val pageCacheMutex = Mutex()
    private val pageCache = mutableMapOf<SearchPageCacheKey, OpenHousePageCacheEntry>()

    override suspend fun ensureAgentProfile(agentId: String, email: String?): Result<Unit> {
        return Result.success(Unit)
    }

    override suspend fun propertyOptions(agentId: String, agencyId: String?): Result<List<OpenHousePropertyOption>> {
        return runCatching {
            client.from(PROPERTIES)
                .select(Columns.list("id", "title", "address_line_1", "city", "region")) {
                    filter {
                        if (agencyId == null) {
                            eq("agent_id", agentId)
                        } else {
                            eq("agency_id", agencyId)
                        }
                    }
                    order("created_at", Order.DESCENDING)
                    limit(OPEN_HOUSE_PROPERTY_OPTION_LIMIT.toLong())
                }
                .decodeList<PropertyOptionRow>()
                .map { property -> property.toDomain() }
        }.onFailure {
            HavnLogger.error("OpenHouses", "Failed to load property options for $agentId", it)
        }
    }

    override suspend fun openHousesForAgent(agentId: String, agencyId: String?): Result<List<OpenHouse>> {
        return openHousePage(agentId, agencyId, query = "", offset = 0, limit = OPEN_HOUSE_PAGE_SIZE)
    }

    override suspend fun openHousePage(
        agentId: String,
        agencyId: String?,
        query: String,
        offset: Int,
        limit: Int
    ): Result<List<OpenHouse>> {
        val normalizedQuery = query.trim()
        val cacheKey = SearchPageCacheKey(agentId, agencyId, normalizedQuery, offset, limit)
        return pageCacheMutex.withLock {
            val nowMs = Clock.System.now().toEpochMilliseconds()
            pageCache[cacheKey]
                ?.takeIf { nowMs - it.savedAtMs <= PAGE_CACHE_TTL_MS }
                ?.let { return@withLock Result.success(it.value) }

            val result = runCatching {
                slowSqlDetail("openHousePage(agent_id=$agentId, agency_id=${agencyId ?: "personal"}, offset=$offset, limit=$limit)") {
                    HavnLogger.debug("OpenHouses", "Loading open houses for agent $agentId")
                    val rows = slowSqlDetail("openHousePage.search(agent_id=$agentId, agency_id=${agencyId ?: "personal"}, offset=$offset, limit=$limit)") {
                        client.postgrest.rpc(
                            function = OPEN_HOUSE_SEARCH_RPC,
                            parameters = SearchPageParams(
                                agencyId = agencyId,
                                query = normalizedQuery.takeIf { it.isNotBlank() },
                                limit = limit,
                                offset = offset
                            )
                        ).decodeList<OpenHouseRow>()
                    }

                    val sortedRows = rows
                        .sortedByDescending { it.startsAt }
                        .take(limit)
                    val embeddedProperties = sortedRows.mapNotNull { row ->
                        row.embeddedPropertyOption()?.let { row.propertyId to it }
                    }.toMap()
                    val missingPropertyIds = sortedRows
                        .map { it.propertyId }
                        .distinct()
                        .filterNot { it in embeddedProperties }
                    val properties = embeddedProperties + slowSqlDetail("openHousePage.properties(missing=${missingPropertyIds.size})") {
                        propertiesFor(missingPropertyIds)
                    }
                    val statsByOpenHouse = slowSqlDetail("openHousePage.stats(count=${sortedRows.size})") {
                        statsForOpenHouses(sortedRows.map { it.id })
                    }

                    sortedRows.map { row ->
                        row.toDomain(
                            property = properties.getValue(row.propertyId),
                            stats = statsByOpenHouse[row.id] ?: OpenHouseStats()
                        )
                    }
                }
            }.onFailure {
                HavnLogger.error("OpenHouses", "Failed to load open houses for $agentId", it)
            }
            result.onSuccess { pageCache[cacheKey] = OpenHousePageCacheEntry(nowMs, it) }
            result
        }
    }

    override suspend fun openHousesForProperty(agentId: String, propertyId: String): Result<List<OpenHouse>> {
        return runCatching {
            val rows = client.from(OPEN_HOUSES)
                .select {
                    filter { eq("property_id", propertyId) }
                    order("starts_at", Order.DESCENDING)
                    limit(100)
                }
                .decodeList<OpenHouseRow>()
            if (rows.isEmpty()) return@runCatching emptyList()
            val property = propertyFor(propertyId)
            val statsByOpenHouse = statsForOpenHouses(rows.map { it.id })
            rows.map { row ->
                row.toDomain(
                    property = property,
                    stats = statsByOpenHouse[row.id] ?: OpenHouseStats()
                )
            }
        }.onFailure {
            HavnLogger.error("OpenHouses", "Failed to load open houses for property $propertyId", it)
        }
    }

    override suspend fun openHouseDetail(agentId: String, openHouseId: String): Result<OpenHouse> {
        return runCatching {
            slowSqlDetail("openHouseDetail(open_house_id=$openHouseId)") {
                val row = slowSqlDetail("openHouseDetail.row(open_house_id=$openHouseId)") {
                    client.from(OPEN_HOUSES)
                        .select {
                            filter {
                                eq("id", openHouseId)
                            }
                            limit(1)
                        }
                        .decodeSingle<OpenHouseRow>()
                }

                val property = openHouseDetailProperty(row.propertyId)
                val stats = openHouseDetailStats(row.id)
                val visitors = openHouseDetailVisitors(row.id)
                row.toDomain(property, stats, visitors)
            }
        }.onFailure {
            HavnLogger.error("OpenHouses", "Failed to load open house $openHouseId", it)
        }
    }

    private suspend inline fun <T> slowSqlDetail(label: String, crossinline block: suspend () -> T): T {
        val startedAtMs = Clock.System.now().toEpochMilliseconds()
        return try {
            block()
        } finally {
            val elapsedMs = Clock.System.now().toEpochMilliseconds() - startedAtMs
            if (elapsedMs > SLOW_DETAIL_SQL_WARNING_THRESHOLD_MS) {
                HavnLogger.warning("SupabaseSQL", "Slow SQL detail $label took ${elapsedMs}ms")
            }
        }
    }

    private suspend fun openHouseDetailProperty(propertyId: String): OpenHousePropertyOption =
        slowSqlDetail("openHouseDetail.property(property_id=$propertyId)") {
            propertyFor(propertyId)
        }

    private suspend fun openHouseDetailStats(openHouseId: String): OpenHouseStats =
        slowSqlDetail("openHouseDetail.stats(open_house_id=$openHouseId)") {
            statsFor(openHouseId)
        }

    private suspend fun openHouseDetailVisitors(openHouseId: String): List<OpenHouseVisitor> =
        slowSqlDetail("openHouseDetail.leads(open_house_id=$openHouseId)") {
            recentVisitorsFor(openHouseId, limit = 100)
        }

    override suspend fun scheduleOpenHouse(agentId: String, draft: OpenHouseDraft, agencyId: String?): Result<OpenHouse> {
        return runCatching {
            HavnLogger.info("OpenHouses", "Scheduling open house for property ${draft.propertyId}")
            val row = client.from(OPEN_HOUSES)
                .insert(draft.toInsertRow(agentId, agencyId)) {
                    select()
                }
                .decodeSingle<OpenHouseRow>()

            clearPageCache()
            row.toDomain(propertyFor(row.propertyId))
        }.onFailure {
            HavnLogger.error("OpenHouses", "Failed to schedule open house for ${draft.propertyId}", it)
        }
    }

    override suspend fun updateOpenHouse(agentId: String, openHouseId: String, draft: OpenHouseDraft): Result<OpenHouse> {
        return runCatching {
            HavnLogger.info("OpenHouses", "Updating open house $openHouseId")
            val row = client.from(OPEN_HOUSES)
                .update(draft.toUpdateRow()) {
                    filter {
                        eq("id", openHouseId)
                    }
                    select()
                }
                .decodeSingle<OpenHouseRow>()

            clearPageCache()
            row.toDomain(propertyFor(row.propertyId), statsFor(row.id), recentVisitorsFor(row.id, limit = 100))
        }.onFailure {
            HavnLogger.error("OpenHouses", "Failed to update open house $openHouseId", it)
        }
    }

    override suspend fun deleteOpenHouse(agentId: String, openHouseId: String): Result<Unit> {
        return runCatching {
            HavnLogger.warning("OpenHouses", "Deleting open house $openHouseId")
            client.from(OPEN_HOUSES).delete {
                filter {
                    eq("id", openHouseId)
                }
            }
            clearPageCache()
            Unit
        }.onFailure {
            HavnLogger.error("OpenHouses", "Failed to delete open house $openHouseId", it)
        }
    }

    override suspend fun startOpenHouse(agentId: String, openHouseId: String): Result<OpenHouse> {
        return updateStatus(agentId, openHouseId, OpenHouseStatus.Live)
    }

    override suspend fun endOpenHouse(agentId: String, openHouseId: String): Result<OpenHouse> {
        return updateStatus(agentId, openHouseId, OpenHouseStatus.Completed)
    }

    override suspend fun submitVisitorSignIn(
        agentId: String,
        openHouseId: String,
        draft: VisitorSignInDraft
    ): Result<OpenHouse> {
        return runCatching {
            HavnLogger.info("OpenHouses", "Submitting visitor sign-in for open house $openHouseId")
            val visitor = client.from(VISITORS)
                .insert(draft.toVisitorInsertRow(agentId, openHouseId)) {
                    select()
                }
                .decodeSingle<VisitorIdRow>()

            client.from(VISITOR_ANSWERS).insert(
                listOf(
                    VisitorAnswerInsertRow(
                        visitorId = visitor.id,
                        ownerAgentId = agentId,
                        questionKey = "working_with_agent",
                        questionText = "Are you working with an agent?",
                        answerText = if (draft.workingWithAgent) "Yes" else "No",
                        sortOrder = 1
                    ),
                    VisitorAnswerInsertRow(
                        visitorId = visitor.id,
                        ownerAgentId = agentId,
                        questionKey = "interest",
                        questionText = "What are you interested in?",
                        answerText = draft.interest.label,
                        sortOrder = 2
                    )
                )
            )

            val row = client.from(OPEN_HOUSES)
                .select {
                    filter { eq("id", openHouseId) }
                    limit(1)
                }
                .decodeSingle<OpenHouseRow>()

            row.toDomain(propertyFor(row.propertyId), statsFor(row.id), recentVisitorsFor(row.id, limit = 8))
        }.onFailure {
            HavnLogger.error("OpenHouses", "Failed to submit visitor sign-in for $openHouseId", it)
        }
    }

    override suspend fun checkInClientQr(
        agentId: String,
        openHouseId: String,
        qrPayload: String
    ): Result<OpenHouse> {
        return runCatching {
            val payload = HavnClientQrPayload.decode(qrPayload).getOrThrow()
            HavnLogger.info("OpenHouses", "Checking in HAVN QR contact ${payload.clientId} for opening $openHouseId")

            val clientRow = client.from(CLIENTS)
                .select(Columns.list("id", "agent_id", "full_name", "email", "phone", "wallet_pass_key")) {
                    filter {
                        eq("id", payload.clientId)
                        eq("agent_id", agentId)
                    }
                    limit(1)
                }
                .decodeSingle<ClientIdentityRow>()
            require(clientRow.walletPassKey == payload.passKey || clientRow.walletPassKey.startsWith(payload.passKey)) {
                "This HAVN Wallet pass key does not match the contact."
            }

            val openHouseRow = client.from(OPEN_HOUSES)
                .select {
                    filter {
                        eq("id", openHouseId)
                    }
                    limit(1)
                }
                .decodeSingle<OpenHouseRow>()

            val existingOpeningContact = client.from(OPENING_CLIENTS)
                .select(Columns.list("id")) {
                    filter {
                        eq("opening_id", openHouseId)
                        eq("contact_id", clientRow.id)
                    }
                    limit(1)
                }
                .decodeSingleOrNull<OpeningClientIdRow>()

            if (existingOpeningContact == null) {
                client.from(OPENING_CLIENTS).insert(
                    OpeningClientInsertRow(
                        openingId = openHouseId,
                        clientId = clientRow.id,
                        invitedByAgentId = agentId,
                        ownerAgentId = agentId
                    )
                )
            } else {
                client.from(OPENING_CLIENTS).update(OpeningClientCheckInRow()) {
                    filter { eq("id", existingOpeningContact.id) }
                }
            }

            val existingLead = client.from(LEADS)
                .select(Columns.list("id", "visitor_id")) {
                    filter {
                        eq("opening_id", openHouseId)
                        eq("contact_id", clientRow.id)
                    }
                    limit(1)
                }
                .decodeSingleOrNull<ClientLeadIdRow>()

            val visitorId = existingLead?.visitorId ?: client.from(VISITORS)
                .insert(
                    VisitorInsertRow(
                        openHouseId = openHouseId,
                        createdByAgentId = agentId,
                        ownerAgentId = agentId,
                        clientId = clientRow.id,
                        fullName = clientRow.fullName,
                        email = clientRow.email,
                        phone = clientRow.phone,
                        representedByAgent = false,
                        temperature = "warm",
                        notes = "Checked in from HAVN Wallet QR"
                    )
                ) {
                    select()
                }
                .decodeSingle<VisitorIdRow>()
                .id

            if (existingLead == null) {
                client.from(LEADS).insert(
                    LeadInsertRow(
                        propertyId = openHouseRow.propertyId,
                        openingId = openHouseId,
                        clientId = clientRow.id,
                        visitorId = visitorId,
                        ownerAgentId = agentId
                    )
                )
            } else if (existingLead.visitorId == null) {
                client.from(LEADS).update(LeadVisitorUpdateRow(visitorId = visitorId)) {
                    filter { eq("id", existingLead.id) }
                }
            }

            openHouseRow.toDomain(propertyFor(openHouseRow.propertyId), statsFor(openHouseRow.id), recentVisitorsFor(openHouseRow.id, limit = 100))
        }.onFailure {
            HavnLogger.error("OpenHouses", "Failed to check in HAVN QR for $openHouseId", it)
        }
    }

    override suspend fun removeClientFromOpenHouse(
        agentId: String,
        openHouseId: String,
        clientId: String
    ): Result<OpenHouse> {
        return runCatching {
            HavnLogger.info("OpenHouses", "Removing contact $clientId from opening $openHouseId")
            val openHouseRow = client.from(OPEN_HOUSES)
                .select {
                    filter {
                        eq("id", openHouseId)
                    }
                    limit(1)
                }
                .decodeSingle<OpenHouseRow>()

            client.from(OPENING_CLIENTS).delete {
                filter {
                    eq("opening_id", openHouseId)
                    eq("contact_id", clientId)
                }
            }
            client.from(LEADS).delete {
                filter {
                    eq("opening_id", openHouseId)
                    eq("contact_id", clientId)
                    eq("owner_agent_id", agentId)
                }
            }
            client.from(VISITORS).delete {
                filter {
                    eq("open_house_id", openHouseId)
                    eq("contact_id", clientId)
                    eq("created_by_agent_id", agentId)
                }
            }

            openHouseRow.toDomain(propertyFor(openHouseRow.propertyId), statsFor(openHouseRow.id), recentVisitorsFor(openHouseRow.id, limit = 100))
        }.onFailure {
            HavnLogger.error("OpenHouses", "Failed to remove contact $clientId from opening $openHouseId", it)
        }
    }

    private suspend fun updateStatus(
        agentId: String,
        openHouseId: String,
        status: OpenHouseStatus
    ): Result<OpenHouse> {
        return runCatching {
            HavnLogger.info("OpenHouses", "Setting open house $openHouseId to ${status.databaseValue}")
            val row = client.from(OPEN_HOUSES)
                .update(OpenHouseStatusUpdateRow(status.databaseValue)) {
                    filter {
                        eq("id", openHouseId)
                    }
                    select()
                }
                .decodeSingle<OpenHouseRow>()

            clearPageCache()
            row.toDomain(propertyFor(row.propertyId), statsFor(row.id), recentVisitorsFor(row.id, limit = 8))
        }.onFailure {
            HavnLogger.error("OpenHouses", "Failed to update open house $openHouseId", it)
        }
    }

    private suspend fun clearPageCache() {
        pageCacheMutex.withLock { pageCache.clear() }
    }

    private suspend fun propertyFor(propertyId: String): OpenHousePropertyOption {
        val photos = photoDataUrls(propertyId)
        return client.from(PROPERTIES)
            .select(Columns.list("id", "title", "address_line_1", "city", "region")) {
                filter { eq("id", propertyId) }
                limit(1)
            }
            .decodeSingle<PropertyOptionRow>()
            .toDomain(photos.firstOrNull(), photos)
    }

    private suspend fun propertiesFor(propertyIds: List<String>): Map<String, OpenHousePropertyOption> {
        if (propertyIds.isEmpty()) return emptyMap()
        val photosByProperty = photoDataUrlsForProperties(propertyIds)
        return client.from(PROPERTIES)
            .select(Columns.list("id", "title", "address_line_1", "city", "region")) {
                filter { isIn("id", propertyIds) }
            }
            .decodeList<PropertyOptionRow>()
            .associate { property ->
                val photos = photosByProperty[property.id].orEmpty()
                property.id to property.toDomain(photos.firstOrNull(), photos)
            }
    }

    private suspend fun heroPhotoDataUrl(propertyId: String): String? {
        return photoDataUrls(propertyId).firstOrNull()
    }

    private suspend fun photoDataUrls(propertyId: String): List<String> {
        return client.from(PHOTOS)
            .select(Columns.list("storage_path", "thumbnail_url", "thumbnail_width", "is_hero", "sort_order", "created_at")) {
                filter { eq("property_id", propertyId) }
                order("is_hero", Order.DESCENDING)
                order("sort_order", Order.ASCENDING)
                order("created_at", Order.DESCENDING)
                limit(8)
            }
            .decodeList<PropertyPhotoPreviewRow>()
            .mapNotNull { it.displayDataUrl }
    }

    private suspend fun photoDataUrlsForProperties(propertyIds: List<String>): Map<String, List<String>> {
        if (propertyIds.isEmpty()) return emptyMap()
        return client.from(PHOTOS)
            .select(Columns.list("property_id", "storage_path", "thumbnail_url", "thumbnail_width", "is_hero", "sort_order", "created_at")) {
                filter {
                    isIn("property_id", propertyIds)
                    eq("is_hero", true)
                }
                order("is_hero", Order.DESCENDING)
                order("sort_order", Order.ASCENDING)
                order("created_at", Order.ASCENDING)
                limit((propertyIds.size * OPEN_HOUSE_PHOTOS_PER_PROPERTY).toLong())
            }
            .decodeList<PropertyPhotoPreviewRow>()
            .groupBy { it.propertyId.orEmpty() }
            .filterKeys { it.isNotBlank() }
            .mapValues { (_, photos) ->
                photos.mapNotNull { it.displayDataUrl }.take(OPEN_HOUSE_PHOTOS_PER_PROPERTY)
            }
    }

    private suspend fun statsFor(openHouseId: String): OpenHouseStats {
        return statsForOpenHouses(listOf(openHouseId))[openHouseId] ?: OpenHouseStats()
    }

    private suspend fun statsForOpenHouses(openHouseIds: List<String>): Map<String, OpenHouseStats> {
        if (openHouseIds.isEmpty()) return emptyMap()
        return client.postgrest.rpc(
            function = OPEN_HOUSE_STATS_RPC,
            parameters = OpenHouseStatsParams(openHouseIds = openHouseIds)
        )
            .decodeList<OpenHouseStatsRow>()
            .associate { row ->
                row.openingId to OpenHouseStats(
                    visitorCount = row.visitorCount,
                    hotCount = row.hotCount,
                    warmCount = row.warmCount,
                    coldCount = row.coldCount
                )
            }
    }

    private suspend fun recentVisitorsFor(openHouseId: String, limit: Long = 5): List<OpenHouseVisitor> {
        return client.postgrest.rpc(
            function = OPEN_HOUSE_RECENT_VISITORS_RPC,
            parameters = OpenHouseRecentVisitorsParams(
                openHouseId = openHouseId,
                limit = limit.toInt()
            )
        )
            .decodeList<OpenHouseRecentVisitorRow>()
            .map { row ->
                row.toDomain()
            }
    }

    private companion object {
        const val OPEN_HOUSE_PAGE_SIZE = 20
        const val OPEN_HOUSE_PROPERTY_OPTION_LIMIT = 100
        const val OPEN_HOUSE_PHOTOS_PER_PROPERTY = 1
        const val PAGE_CACHE_TTL_MS = 15_000L
        const val SLOW_DETAIL_SQL_WARNING_THRESHOLD_MS = 1_000L
        const val OPEN_HOUSE_SEARCH_RPC = "search_open_houses_for_current_user"
        const val OPEN_HOUSE_STATS_RPC = "open_house_stats_for_current_user"
        const val OPEN_HOUSE_RECENT_VISITORS_RPC = "open_house_recent_visitors_for_current_user"
        const val AGENTS = "agents"
        const val PROPERTIES = "properties"
        const val PHOTOS = "property_photos"
        const val OPEN_HOUSES = "open_houses"
        const val VISITORS = "visitors"
        const val VISITOR_ANSWERS = "visitor_answers"
        const val CLIENTS = "contacts"
        const val OPENING_CLIENTS = "opening_contacts"
        const val LEADS = "leads"
    }
}

@Serializable
private data class AgentIdRow(val id: String)

@Serializable
private data class PropertyIdRow(val id: String)

@Serializable
private data class AgentInsertRow(
    val id: String,
    @SerialName("full_name") val fullName: String,
    val email: String
)

@Serializable
private data class PropertyOptionRow(
    val id: String,
    val title: String,
    @SerialName("address_line_1") val addressLine1: String,
    val city: String,
    val region: String
) {
    fun toDomain(thumbnailDataUrl: String? = null, photoDataUrls: List<String> = emptyList()): OpenHousePropertyOption = OpenHousePropertyOption(
        id = id,
        title = title,
        address = "$addressLine1, $city, $region",
        thumbnailDataUrl = thumbnailDataUrl,
        photoDataUrls = photoDataUrls
    )
}

@Serializable
private data class PropertyPhotoPreviewRow(
    @SerialName("property_id") val propertyId: String? = null,
    @SerialName("storage_path") val storagePath: String? = null,
    @SerialName("thumbnail_width") val thumbnailWidth: Int? = null,
    @SerialName("thumbnail_url") val thumbnailDataUrl: String? = null
) {
    val displayDataUrl: String?
        get() = havnDisplayPhotoSource(storagePath, thumbnailDataUrl, thumbnailWidth)
}

@Serializable
private data class OpenHouseRow(
    val id: String,
    @SerialName("property_id") val propertyId: String,
    @SerialName("host_agent_id") val hostAgentId: String,
    val title: String? = null,
    @SerialName("starts_at") val startsAt: String,
    @SerialName("ends_at") val endsAt: String,
    val status: String,
    @SerialName("access_notes") val accessNotes: String? = null,
    @SerialName("internal_notes") val internalNotes: String? = null,
    @SerialName("property_title") val propertyTitle: String? = null,
    @SerialName("property_address_line_1") val propertyAddressLine1: String? = null,
    @SerialName("property_city") val propertyCity: String? = null,
    @SerialName("property_region") val propertyRegion: String? = null,
    @SerialName("property_hero_storage_path") val propertyHeroStoragePath: String? = null,
    @SerialName("property_hero_thumbnail_url") val propertyHeroThumbnailDataUrl: String? = null,
    @SerialName("property_hero_thumbnail_width") val propertyHeroThumbnailWidth: Int? = null,
    @SerialName("created_at") val createdAt: String? = null,
    @SerialName("updated_at") val updatedAt: String? = null
) {
    fun embeddedPropertyOption(): OpenHousePropertyOption? {
        val title = propertyTitle?.takeIf { it.isNotBlank() } ?: return null
        val addressLine = propertyAddressLine1?.takeIf { it.isNotBlank() } ?: return null
        val city = propertyCity?.takeIf { it.isNotBlank() } ?: return null
        val region = propertyRegion?.takeIf { it.isNotBlank() } ?: return null
        val thumbnail = havnDisplayPhotoSource(
            propertyHeroStoragePath,
            propertyHeroThumbnailDataUrl,
            propertyHeroThumbnailWidth
        )
        return OpenHousePropertyOption(
            id = propertyId,
            title = title,
            address = "$addressLine, $city, $region",
            thumbnailDataUrl = thumbnail,
            photoDataUrls = listOfNotNull(thumbnail)
        )
    }

    fun toDomain(
        property: OpenHousePropertyOption,
        stats: OpenHouseStats = OpenHouseStats(),
        visitors: List<OpenHouseVisitor> = emptyList()
    ): OpenHouse = OpenHouse(
        id = id,
        propertyId = propertyId,
        propertyTitle = property.title,
        propertyAddress = property.address,
        hostAgentId = hostAgentId,
        propertyThumbnailDataUrl = property.thumbnailDataUrl,
        propertyPhotoDataUrls = property.photoDataUrls.ifEmpty { listOfNotNull(property.thumbnailDataUrl) },
        title = title,
        startsAt = startsAt,
        endsAt = endsAt,
        status = OpenHouseStatus.fromDatabase(status),
        accessNotes = accessNotes,
        internalNotes = internalNotes,
        createdAt = createdAt,
        updatedAt = updatedAt,
        stats = stats,
        recentSignIns = visitors
    )
}

@Serializable
private data class OpenHouseInsertRow(
    @SerialName("property_id") val propertyId: String,
    @SerialName("host_agent_id") val hostAgentId: String,
    @SerialName("owner_agent_id") val ownerAgentId: String,
    @SerialName("agency_id") val agencyId: String? = null,
    val title: String? = null,
    @SerialName("starts_at") val startsAt: String,
    @SerialName("ends_at") val endsAt: String,
    @SerialName("access_notes") val accessNotes: String? = null,
    @SerialName("internal_notes") val internalNotes: String? = null,
    val status: String = "scheduled"
)

@Serializable
private data class OpenHouseUpdateRow(
    @SerialName("property_id") val propertyId: String,
    val title: String? = null,
    @SerialName("starts_at") val startsAt: String,
    @SerialName("ends_at") val endsAt: String,
    @SerialName("access_notes") val accessNotes: String? = null,
    @SerialName("internal_notes") val internalNotes: String? = null
)

@Serializable
private data class OpenHouseStatusUpdateRow(val status: String)

@Serializable
private data class VisitorTemperatureRow(
    val id: String,
    val temperature: String
)

@Serializable
private data class OpeningLeadStatsRow(
    val id: String,
    @SerialName("opening_id") val openingId: String,
    val temperature: String? = null
)

@Serializable
private data class VisitorIdRow(val id: String)

@Serializable
private data class VisitorInsertRow(
    @SerialName("open_house_id") val openHouseId: String,
    @SerialName("created_by_agent_id") val createdByAgentId: String,
    @SerialName("owner_agent_id") val ownerAgentId: String,
    @SerialName("contact_id") val clientId: String? = null,
    @SerialName("full_name") val fullName: String,
    val email: String? = null,
    val phone: String? = null,
    @SerialName("represented_by_agent") val representedByAgent: Boolean,
    val temperature: String,
    val notes: String? = null
)

@Serializable
private data class ClientIdentityRow(
    val id: String,
    @SerialName("agent_id") val agentId: String,
    @SerialName("full_name") val fullName: String,
    val email: String? = null,
    val phone: String? = null,
    @SerialName("wallet_pass_key") val walletPassKey: String
)

@Serializable
private data class OpeningClientIdRow(val id: String)

@Serializable
private data class OpeningClientInsertRow(
    @SerialName("opening_id") val openingId: String,
    @SerialName("contact_id") val clientId: String,
    @SerialName("invited_by_agent_id") val invitedByAgentId: String,
    @SerialName("owner_agent_id") val ownerAgentId: String,
    val status: String = "checked_in",
    @SerialName("checked_in_at") val checkedInAt: String = "now"
)

@Serializable
private data class OpeningClientCheckInRow(
    val status: String = "checked_in",
    @SerialName("checked_in_at") val checkedInAt: String = "now"
)

@Serializable
private data class ClientLeadIdRow(
    val id: String,
    @SerialName("visitor_id") val visitorId: String? = null
)

@Serializable
private data class LeadInsertRow(
    @SerialName("property_id") val propertyId: String,
    @SerialName("opening_id") val openingId: String,
    @SerialName("contact_id") val clientId: String,
    @SerialName("visitor_id") val visitorId: String,
    @SerialName("owner_agent_id") val ownerAgentId: String,
    val temperature: String = "warm",
    val status: String = "new",
    val source: String = "havn_wallet_qr",
    val notes: String = "Created by scanning a HAVN contact QR."
)

@Serializable
private data class LeadVisitorUpdateRow(
    @SerialName("visitor_id") val visitorId: String
)

@Serializable
private data class VisitorAnswerInsertRow(
    @SerialName("visitor_id") val visitorId: String,
    @SerialName("owner_agent_id") val ownerAgentId: String,
    @SerialName("question_key") val questionKey: String,
    @SerialName("question_text") val questionText: String,
    @SerialName("answer_text") val answerText: String,
    @SerialName("sort_order") val sortOrder: Int
)

@Serializable
private data class VisitorRow(
    val id: String,
    @SerialName("contact_id") val clientId: String? = null,
    @SerialName("full_name") val fullName: String,
    val email: String? = null,
    val phone: String? = null,
    val temperature: String,
    @SerialName("checked_in_at") val checkedInAt: String
) {
    fun toDomain(): OpenHouseVisitor = OpenHouseVisitor(
        id = id,
        clientId = clientId,
        fullName = fullName,
        email = email,
        phone = phone,
        temperature = LeadTemperature.fromDatabase(temperature),
        checkedInAt = checkedInAt
    )
}

@Serializable
private data class OpeningLeadRow(
    val id: String,
    @SerialName("contact_id") val clientId: String,
    @SerialName("visitor_id") val visitorId: String? = null,
    val temperature: String? = null,
    @SerialName("created_at") val createdAt: String? = null
)

@Serializable
private data class OpeningLeadClientRow(
    val id: String,
    @SerialName("full_name") val fullName: String,
    val email: String? = null,
    val phone: String? = null
)

@Serializable
private data class OpeningLeadVisitorRow(
    val id: String,
    @SerialName("checked_in_at") val checkedInAt: String
)

@Serializable
private data class SearchPageParams(
    @SerialName("p_agency_id") val agencyId: String?,
    @SerialName("p_query") val query: String?,
    @SerialName("p_limit") val limit: Int,
    @SerialName("p_offset") val offset: Int
)

private data class SearchPageCacheKey(
    val agentId: String,
    val agencyId: String?,
    val query: String,
    val offset: Int,
    val limit: Int
)

private data class OpenHousePageCacheEntry(
    val savedAtMs: Long,
    val value: List<OpenHouse>
)

@Serializable
private data class OpenHouseStatsParams(
    @SerialName("p_open_house_ids") val openHouseIds: List<String>
)

@Serializable
private data class OpenHouseStatsRow(
    @SerialName("opening_id") val openingId: String,
    @SerialName("visitor_count") val visitorCount: Int = 0,
    @SerialName("hot_count") val hotCount: Int = 0,
    @SerialName("warm_count") val warmCount: Int = 0,
    @SerialName("cold_count") val coldCount: Int = 0
)

@Serializable
private data class OpenHouseRecentVisitorsParams(
    @SerialName("p_open_house_id") val openHouseId: String,
    @SerialName("p_limit") val limit: Int
)

@Serializable
private data class OpenHouseRecentVisitorRow(
    val id: String,
    @SerialName("contact_id") val clientId: String? = null,
    @SerialName("visitor_id") val visitorId: String? = null,
    @SerialName("full_name") val fullName: String,
    val email: String? = null,
    val phone: String? = null,
    val temperature: String? = null,
    @SerialName("checked_in_at") val checkedInAt: String
) {
    fun toDomain(): OpenHouseVisitor = OpenHouseVisitor(
        id = id,
        clientId = clientId,
        visitorId = visitorId,
        fullName = fullName,
        email = email,
        phone = phone,
        temperature = LeadTemperature.fromDatabase(temperature),
        checkedInAt = checkedInAt
    )
}

private fun OpenHouseDraft.toInsertRow(agentId: String, agencyId: String?): OpenHouseInsertRow = OpenHouseInsertRow(
    propertyId = propertyId,
    hostAgentId = agentId,
    ownerAgentId = agentId,
    agencyId = agencyId,
    title = title.trim().takeIf { it.isNotBlank() },
    startsAt = startsAt.trim(),
    endsAt = endsAt.trim(),
    accessNotes = accessNotes.trim().takeIf { it.isNotBlank() },
    internalNotes = internalNotes.trim().takeIf { it.isNotBlank() }
)

private fun OpenHouseDraft.toUpdateRow(): OpenHouseUpdateRow = OpenHouseUpdateRow(
    propertyId = propertyId,
    title = title.trim().takeIf { it.isNotBlank() },
    startsAt = startsAt,
    endsAt = endsAt,
    accessNotes = accessNotes.trim().takeIf { it.isNotBlank() },
    internalNotes = internalNotes.trim().takeIf { it.isNotBlank() }
)

private fun VisitorSignInDraft.toVisitorInsertRow(agentId: String, openHouseId: String): VisitorInsertRow {
    val leadTemperature = when (interest) {
        VisitorInterest.Buying,
        VisitorInterest.Relocating -> if (workingWithAgent) "warm" else "hot"
        VisitorInterest.Investing -> "warm"
        VisitorInterest.Browsing -> "cold"
    }
    return VisitorInsertRow(
        openHouseId = openHouseId,
        createdByAgentId = agentId,
        ownerAgentId = agentId,
        fullName = fullName.trim(),
        email = email?.trim()?.takeIf { it.isNotBlank() },
        phone = phone?.trim()?.takeIf { it.isNotBlank() },
        representedByAgent = workingWithAgent,
        temperature = leadTemperature,
        notes = "Interest: ${interest.label}; working with agent: ${if (workingWithAgent) "yes" else "no"}"
    )
}
